import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, ShoppingCart, Users, Calendar, Download } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface RelatorioVendas {
  data: string;
  total: number;
  quantidade: number;
}

interface RelatorioProdutos {
  nome: string;
  quantidade_vendida: number;
  receita: number;
}

interface RelatorioCategorias {
  categoria: string;
  quantidade: number;
  receita: number;
}

const RelatoriosPage: React.FC = () => {
  const [periodo, setPeriodo] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [vendas, setVendas] = useState<RelatorioVendas[]>([]);
  const [produtos, setProdutos] = useState<RelatorioProdutos[]>([]);
  const [categorias, setCategorias] = useState<RelatorioCategorias[]>([]);
  const [resumo, setResumo] = useState({
    totalVendas: 0,
    totalPedidos: 0,
    mediaTicket: 0,
    produtosVendidos: 0
  });

  useEffect(() => {
    fetchRelatorios();
  }, [periodo]);

  const fetchRelatorios = async () => {
    setLoading(true);
    try {
      const [vendasRes, produtosRes, categoriasRes, resumoRes] = await Promise.all([
        api.get(`/api/relatorios/vendas?periodo=${periodo}`),
        api.get(`/api/relatorios/produtos?periodo=${periodo}`),
        api.get(`/api/relatorios/categorias?periodo=${periodo}`),
        api.get(`/api/relatorios/resumo?periodo=${periodo}`)
      ]);

      setVendas(Array.isArray(vendasRes.data) ? vendasRes.data : []);
      setProdutos(Array.isArray(produtosRes.data) ? produtosRes.data : []);
      setCategorias(Array.isArray(categoriasRes.data) ? categoriasRes.data : []);
      setResumo(resumoRes.data || {
        totalVendas: 0,
        totalPedidos: 0,
        mediaTicket: 0,
        produtosVendidos: 0
      });
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const exportarRelatorio = async (tipo: string) => {
    try {
      const response = await api.get(`/api/relatorios/exportar/${tipo}?periodo=${periodo}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${tipo}_${periodo}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Relatórios e Analytics
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Análise detalhada do seu negócio
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="input-field"
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
            <option value="1y">Último ano</option>
          </select>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total de Vendas
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                R$ {(Number(resumo?.totalVendas) || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total de Pedidos
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Number(resumo?.totalPedidos) || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Ticket Médio
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                R$ {(Number(resumo?.mediaTicket) || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Produtos Vendidos
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Number(resumo?.produtosVendidos) || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos e Tabelas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendas por Período */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Vendas por Período
            </h3>
            <button
              onClick={() => exportarRelatorio('vendas')}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <Download className="h-4 w-4" />
              Exportar
            </button>
          </div>
          <div className="space-y-3">
            {vendas.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhuma venda no período</p>
            ) : (
              vendas.map((venda, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {venda.data ? new Date(venda.data).toLocaleDateString('pt-BR') : 'Data inválida'}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      R$ {(Number(venda.total) || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {venda.quantidade} pedidos
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Produtos Mais Vendidos */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Produtos Mais Vendidos
            </h3>
            <button
              onClick={() => exportarRelatorio('produtos')}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <Download className="h-4 w-4" />
              Exportar
            </button>
          </div>
          <div className="space-y-3">
            {produtos.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhum produto vendido</p>
            ) : (
              produtos.slice(0, 5).map((produto, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {produto.nome}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {produto.quantidade_vendida} unidades
                    </p>
                  </div>
                  <p className="text-sm font-medium text-green-600">
                    R$ {(Number(produto.receita) || 0).toFixed(2)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Vendas por Categoria */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Vendas por Categoria
            </h3>
            <button
              onClick={() => exportarRelatorio('categorias')}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <Download className="h-4 w-4" />
              Exportar
            </button>
          </div>
          <div className="space-y-3">
            {categorias.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria registrada</p>
            ) : (
              categorias.map((categoria, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {categoria.categoria}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {categoria.quantidade} vendas
                    </p>
                  </div>
                  <p className="text-sm font-medium text-green-600">
                    R$ {(Number(categoria.receita) || 0).toFixed(2)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Insights */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Insights
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                Melhor Dia da Semana
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {(() => {
                  if (vendas.length === 0) return 'Sem dados suficientes';
                  try {
                    const melhorDia = vendas.reduce((max, v) => Number(v.total) > Number(max.total) ? v : max);
                    return melhorDia.data ? new Date(melhorDia.data).toLocaleDateString('pt-BR', { weekday: 'long' }) : 'Data inválida';
                  } catch (e) {
                    return 'Erro ao calcular';
                  }
                })()}
              </p>
            </div>
            
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">
                Produto Estrela
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                {produtos.length > 0 ? produtos[0].nome : 'Sem dados suficientes'}
              </p>
            </div>

            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">
                Crescimento
              </h4>
              <p className="text-sm text-purple-700 dark:text-purple-300">
                {(() => {
                  if (vendas.length < 2) return 'Sem dados suficientes';
                  const primeira = Number(vendas[0].total) || 0;
                  const ultima = Number(vendas[vendas.length - 1].total) || 0;
                  if (primeira === 0) return 'N/A';
                  return `${((ultima - primeira) / primeira * 100).toFixed(1)}%`;
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatoriosPage;
